import { SerializedData } from 'bernie-core';
import {
  CarSearchCriteriaInput,
  DateInput,
  DateTimeInput,
  FlightSearchCriteriaInput,
  FlightsJourneyAvailableFares,
  FlightsJourneyCriteriaInput,
  FlightsShoppingPwaFlightSearchResultsFlightsSearch,
  FlightsTravelerDetailsInput,
  InitiateMultiItemSession,
  MultiItemContextInput,
  MultiItemSearchContextInput,
  MultiItemSelectProductsFragment,
  MultiItemSelectPackageFragment,
  SelectProducts,
  SelectPackage,
  TravelerDetailsInput
} from '__generated__/api';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { RequestOptions } from 'bernie-graphql';
import { clientInfoHeader } from 'utility/header-utils';
import { ContextInput } from 'bernie-context/dist/platform/bex-api-context';
import { observable } from 'mobx';
import SelectPackageMultiItemSearchContextCreatedResponseInlineFragment = SelectPackage.MultiItemSearchContextCreatedResponseInlineFragment;
import SelectProductsMultiItemSearchContextCreatedResponseInlineFragment = SelectProducts.MultiItemSearchContextCreatedResponseInlineFragment;
import FlightsOfferNaturalKeys = FlightsJourneyAvailableFares.FlightsOfferNaturalKeys;
import SearchCriteria = FlightsShoppingPwaFlightSearchResultsFlightsSearch.Variables;
import { BaseBexApiStore } from './bex-api-store';
import MultiItemAction = FlightsJourneyAvailableFares.MultiItemAction;

export class MultiItemStore extends BaseBexApiStore {
  @observable public multiItemContext: MultiItemContextInput;
  public readonly RETRY_COUNT = 3;
  public readonly DELAY_MS = 100;

  public hydrate(data: SerializedData): void {
    super.hydrate(data);
    this.bexApiSource.clientConfigs.bexApiUrl = '/graphql';
  }

  public initiateMultiItemSession(
    variables: InitiateMultiItemSession.Variables,
    options: RequestOptions
  ): Promise<MultiItemContextInput> {
    const { appName, region } = this.bexApiSource.clientConfigs;
    this.bexApiSource.headers = clientInfoHeader(this.bexApiSource.headers, appName, region);

    return this.bexApiSource.operations.initiateMultiItemSession(variables, options).then(({ data, errors }) => {
      if (errors) {
        this.logger.logEvent(new SystemEvent(SystemEventLevel.ERROR, 'INITIATE_MULTIITEM_SESSION_FAILURE'), errors);

        return Promise.reject(errors);
      }
      if (
        data.multiItemShopping.initiate.__typename === 'MultiItemSearchContextCreatedResponse' &&
        data.multiItemShopping.initiate.shoppingContext.multiItem
      ) {
        Object.assign(this.multiItemContext, data.multiItemShopping.initiate.shoppingContext.multiItem);

        return Promise.resolve(this.multiItemContext);
      }

      return Promise.reject([data.multiItemShopping.initiate.message ?? 'error while initiating multiitem session']);
    });
  }

  public buildInitiateMultiItemSessionVariables(
    searchCriteria: SearchCriteria,
    context: ContextInput
  ): InitiateMultiItemSession.Variables {
    return {
      context: context,
      searchContext: this.buildMultiItemSearchContext(searchCriteria)
    };
  }

  private buildMultiItemSearchContext(searchCriteria: SearchCriteria): MultiItemSearchContextInput {
    return {
      cars: this.buildCarsSearchCriteria(searchCriteria),
      flights: this.buildFlightsSearchCriteria(searchCriteria)
    };
  }

  private buildCarsSearchCriteria(searchCriteria: SearchCriteria): CarSearchCriteriaInput[] {
    return [
      {
        primary: {
          pickUpDateTime: this.formatCarDateTime(searchCriteria.journeyCriteria[0].departureDate),
          dropOffDateTime: this.formatCarDateTime(searchCriteria.journeyCriteria[1].departureDate),
          pickUpLocation: {}
        }
      }
    ];
  }

  private formatCarDateTime(date: DateInput): DateTimeInput {
    const defaultPickUpTime = {
      hour: 10,
      minute: 30,
      second: 0
    };

    return {
      ...date,
      ...defaultPickUpTime
    };
  }

  private buildFlightsSearchCriteria(searchCriteria: SearchCriteria): FlightSearchCriteriaInput[] {
    const multiItemAirlinePreferences =
      searchCriteria.searchPreferences.airline === '*' ? null : searchCriteria.searchPreferences.airline;
    searchCriteria.searchPreferences.airline = multiItemAirlinePreferences;

    return [
      {
        primary: {
          tripType: searchCriteria.flightsSearchContext.tripType,
          travelers: this.buildFlightsTravelers(searchCriteria.travelerDetails),
          journeyCriterias: searchCriteria.journeyCriteria as FlightsJourneyCriteriaInput[],
          searchPreferences: searchCriteria.searchPreferences
        }
      }
    ];
  }

  private buildFlightsTravelers(
    travelerDetails: FlightsTravelerDetailsInput | FlightsTravelerDetailsInput[]
  ): TravelerDetailsInput[] {
    const travelers: TravelerDetailsInput[] = [];
    (travelerDetails as FlightsTravelerDetailsInput[]).forEach(item => {
      travelers.push(...this.buildTravelerDetailsInput(item));
    });

    return travelers;
  }

  private buildTravelerDetailsInput(travelers: FlightsTravelerDetailsInput): TravelerDetailsInput[] {
    const flightsTravelerDetailsInputs = [];
    const count = travelers.count;
    for (let index = 0; index < count; index++) {
      flightsTravelerDetailsInputs.push({
        age: travelers.ages?.[index] ?? null,
        type: travelers.travelerType
      });
    }

    return flightsTravelerDetailsInputs;
  }

  public async selectProductsWithFlightsVariables(
    flightsOfferNaturalKeys: FlightsOfferNaturalKeys,
    multiItemPriceToken: string,
    sessionId: string,
    context: ContextInput,
    requestOptions: RequestOptions
  ): Promise<SelectProductsMultiItemSearchContextCreatedResponseInlineFragment> {
    const omitTypename: (key: string, value: string) => string = (key, value) =>
      key === '__typename' ? undefined : value;
    const naturalKeys = flightsOfferNaturalKeys?.packagedProductsNaturalKeys ?? [];
    const { carNaturalKeys, propertyNaturalKeys } = naturalKeys.reduce(
      (obj, nextNaturalKey) => {
        if (nextNaturalKey.__typename === 'CarNaturalKey') {
          obj.carNaturalKeys.push(JSON.parse(JSON.stringify(nextNaturalKey), omitTypename));
        } else if (nextNaturalKey.__typename === 'PropertyNaturalKey') {
          obj.propertyNaturalKeys.push(JSON.parse(JSON.stringify(nextNaturalKey), omitTypename));
        }

        return obj;
      },
      {
        carNaturalKeys: [],
        propertyNaturalKeys: []
      }
    );

    const variables = {
      context,
      sessionId,
      priceToken: multiItemPriceToken,
      properties: propertyNaturalKeys,
      flights: [flightsOfferNaturalKeys.flightNaturalKey],
      cars: carNaturalKeys
    };

    return this.selectProducts(variables, requestOptions);
  }

  public async selectProducts(
    variables: SelectProducts.Variables,
    options: RequestOptions,
    retryCount: number = this.RETRY_COUNT,
    delayMS: number = this.DELAY_MS
  ): Promise<SelectProductsMultiItemSearchContextCreatedResponseInlineFragment> {
    const { appName, region } = this.bexApiSource.clientConfigs;
    this.bexApiSource.headers = clientInfoHeader(this.bexApiSource.headers, appName, region);

    return this.bexApiSource.operations
      .selectProducts(variables, options)
      .then(({ data, errors }) => {
        const { multiItemShopping } = data;
        const { selectProducts } = multiItemShopping;
        if (
          selectProducts.__typename === 'MultiItemSearchContextCreatedResponse' &&
          selectProducts.redirectUrl &&
          selectProducts.shoppingContext.multiItem
        ) {
          if (retryCount < this.RETRY_COUNT) {
            this.logger.logEvent(
              new SystemEvent(SystemEventLevel.INFO, 'SELECT_PRODUCTS_RETRY_SUCCESS'),
              this.logValue(JSON.stringify(data), retryCount, delayMS)
            );
          }

          return selectProducts;
        } else if (selectProducts.__typename === 'MultiItemSearchContextErrorResponse' && selectProducts.message) {
          return Promise.reject(errors || [selectProducts.message]);
        }

        return Promise.reject(errors || ['error while selecting products']);
      })
      .catch(async error => {
        this.logger.logEvent(
          new SystemEvent(SystemEventLevel.ERROR, 'SELECT_PRODUCTS_FAILURE'),
          this.logValue(error.toString(), retryCount, delayMS)
        );
        if (retryCount > 0) {
          await this.delay(delayMS);
          this.logger.logEvent(
            new SystemEvent(SystemEventLevel.WARN, 'SELECT_PRODUCTS_RETRY'),
            this.logValue(error.toString(), retryCount, delayMS)
          );

          return this.selectProducts(variables, options, retryCount - 1, delayMS * 2);
        }

        return Promise.reject(error);
      });
  }

  public async executeMultiItemAction(
    multiItemAction: MultiItemAction,
    sessionId: string,
    context: ContextInput,
    pageId: string
  ): Promise<string> {
    const requestOptions: RequestOptions = {
      pageId: pageId,
      extraHeaders: { 'Device-User-Agent-ID': context.identity.duaid }
    };

    if (multiItemAction.__typename === 'MultiItemSelectPackageAction') {
      const selectPackageVariables: SelectPackage.Variables = {
        context: context,
        packageOfferId: multiItemAction.packageOfferId,
        sessionId: sessionId
      };

      return this.selectPackage(selectPackageVariables, requestOptions)
        .then(selectPackage => selectPackage.redirectUrl)
        .catch(error => {
          return Promise.reject(error);
        });
    }

    const { flightsOfferNaturalKeys, multiItemPriceToken, multiItemSessionId } = multiItemAction;

    return this.selectProductsWithFlightsVariables(
      flightsOfferNaturalKeys,
      multiItemPriceToken,
      multiItemSessionId,
      context,
      requestOptions
    )
      .then(selectProducts => selectProducts.redirectUrl)
      .catch(error => {
        return Promise.reject(error);
      });
  }

  public async executeMultiItemSelectProduct(
    clientAction: MultiItemSelectProductsFragment,
    context: ContextInput,
    pageId: string
  ): Promise<string> {
    const requestOptions: RequestOptions = {
      pageId: pageId,
      extraHeaders: { 'Device-User-Agent-ID': context.identity.duaid }
    };

    const { flightsOfferNaturalKeys, multiItemPriceToken, multiItemSessionId } = clientAction;

    return this.selectProductsWithFlightsVariables(
      flightsOfferNaturalKeys,
      multiItemPriceToken,
      multiItemSessionId,
      context,
      requestOptions
    )
      .then(selectProducts => selectProducts.redirectUrl)
      .catch(error => {
        return Promise.reject(error);
      });
  }

  public async executeMultiItemSelectPackage(
    clientAction: MultiItemSelectPackageFragment,
    sessionId: string,
    context: ContextInput,
    pageId: string
  ): Promise<string> {
    const requestOptions: RequestOptions = {
      pageId: pageId,
      extraHeaders: { 'Device-User-Agent-ID': context.identity.duaid }
    };

    const selectPackageVariables: SelectPackage.Variables = {
      context: context,
      packageOfferId: clientAction.packageOfferId,
      sessionId: sessionId
    };

    return this.selectPackage(selectPackageVariables, requestOptions)
      .then(selectPackage => selectPackage.redirectUrl)
      .catch(error => {
        return Promise.reject(error);
      });
  }

  public async selectPackage(
    variables: SelectPackage.Variables,
    options: RequestOptions,
    retryCount: number = this.RETRY_COUNT,
    delayMS: number = this.DELAY_MS
  ): Promise<SelectPackageMultiItemSearchContextCreatedResponseInlineFragment> {
    const { appName, region } = this.bexApiSource.clientConfigs;
    this.bexApiSource.headers = clientInfoHeader(this.bexApiSource.headers, appName, region);

    return this.bexApiSource.operations
      .selectPackage(variables, options)
      .then(({ data, errors }) => {
        const { multiItemShopping } = data;
        const { selectPackage } = multiItemShopping;
        if (
          selectPackage.__typename === 'MultiItemSearchContextCreatedResponse' &&
          selectPackage.redirectUrl &&
          selectPackage.action.shoppingContext.multiItem
        ) {
          if (retryCount < this.RETRY_COUNT) {
            this.logger.logEvent(
              new SystemEvent(SystemEventLevel.INFO, 'SELECT_PACKAGE_RETRY_SUCCESS'),
              this.logValue(JSON.stringify(data), retryCount, delayMS)
            );
          }

          return selectPackage;
        } else if (selectPackage.__typename === 'MultiItemSearchContextErrorResponse' && selectPackage.message) {
          return Promise.reject(errors || [selectPackage.message]);
        }

        return Promise.reject(errors || ['error while selecting package']);
      })
      .catch(async error => {
        this.logger.logEvent(
          new SystemEvent(SystemEventLevel.ERROR, 'SELECT_PACKAGE_FAILURE'),
          this.logValue(error.toString(), retryCount, delayMS)
        );
        if (retryCount > 0) {
          await this.delay(delayMS);
          this.logger.logEvent(
            new SystemEvent(SystemEventLevel.WARN, 'SELECT_PACKAGE_RETRY'),
            this.logValue(error.toString(), retryCount, delayMS)
          );

          return this.selectPackage(variables, options, retryCount - 1, delayMS * 2);
        }

        return Promise.reject(error);
      });
  }

  public async delay(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  logValue(data: string, retryCount: number, delayMS: number) {
    const retryCountLogValue = `Retry_count=${retryCount}`;
    const delayMSLogValue = `Delay_time_in_ms=${delayMS}`;

    return `${data}, ${retryCountLogValue}, ${delayMSLogValue}`;
  }
}

MultiItemStore.DEFAULTS = {
  multiItemContext: {}
};
